import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2, Phone, Home, Building2 } from "lucide-react";
import { WHATSAPP_LINK, PHONE_DISPLAY, PHONE_LINK } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = ["Cleaning", "Window Cleaning", "Gardening & Lawn", "Painting", "Repairs & Odd Jobs"];

export default function QuoteForm() {
  const [form, setForm] = useState({ name: "", phone: "", location: "", details: "" });
  const [propertyType, setPropertyType] = useState("home");
  const [services, setServices] = useState([]);
  const [sending, setSending] = useState(false);

  const toggleService = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, property_type: propertyType, services });
      toast.success("Quote request sent! We'll get back to you shortly.");
      setForm({ name: "", phone: "", location: "", details: "" });
      setServices([]);
      setPropertyType("home");
    } catch {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-[#E4DDD3] bg-white px-5 py-3.5 text-sm text-[#1A1C18] outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-[#7B867C]/70 focus:border-[#1C3B2B] focus:ring-4 focus:ring-[#1C3B2B]/10";

  return (
    <section id="quote" className="relative overflow-hidden bg-[#F3EDE2] py-24 sm:py-32">
      <div className="pointer-events-none absolute -right-24 top-0 h-[420px] w-[420px] rounded-full bg-[#E2EFEA] blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <p className="font-jbm text-xs font-semibold uppercase tracking-[0.3em] text-[#C85A32]">Free quote</p>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#1C3B2B] sm:text-4xl lg:text-5xl">
            Tell us the job. We'll handle the rest.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#525B53] sm:text-lg">
            No fixed price lists — every property is different. Send your request and we'll reply with a
            fair, tailored quote. Prefer chatting? We're one tap away.
          </p>

          <div className="mt-10 space-y-4">
            <a
              data-testid="quote-whatsapp-button"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="group flex items-center justify-between rounded-3xl bg-[#25D366] p-6 text-white shadow-[0_20px_45px_-20px_rgba(37,211,102,0.7)] transition-[background-color,transform] duration-300 hover:bg-[#20BA5A] hover:-translate-y-1"
            >
              <span className="flex items-center gap-4">
                <MessageCircle size={26} />
                <span>
                  <span className="font-display block text-lg font-bold">Chat instantly on WhatsApp</span>
                  <span className="text-sm text-white/85">{PHONE_DISPLAY}</span>
                </span>
              </span>
              <Send size={20} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              data-testid="quote-call-button"
              href={PHONE_LINK}
              className="flex items-center gap-4 rounded-3xl border border-[#E4DDD3] bg-white p-6 text-[#1C3B2B] transition-transform duration-300 hover:-translate-y-1"
            >
              <Phone size={24} />
              <span>
                <span className="font-display block text-lg font-bold">Call us directly</span>
                <span className="text-sm text-[#525B53]">{PHONE_DISPLAY} · Mon–Sat, 8:00–18:00</span>
              </span>
            </a>
          </div>
        </motion.div>

        <motion.form
          data-testid="quote-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="rounded-3xl border border-[#E4DDD3] bg-white p-7 shadow-[0_35px_70px_-35px_rgba(28,59,43,0.4)] sm:p-10 lg:col-span-7"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-[#525B53]">Full name</label>
              <input
                data-testid="quote-form-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-[#525B53]">Phone / WhatsApp</label>
              <input
                data-testid="quote-form-phone-input"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+254 7XX XXX XXX"
                className={inputCls}
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-[#525B53]">
              Property location (Shanzu / Mombasa)
            </label>
            <input
              data-testid="quote-form-location-input"
              required
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="e.g. Serena Road, Shanzu"
              className={inputCls}
            />
          </div>

          <div className="mt-6">
            <label className="mb-3 block text-xs font-semibold uppercase tracking-[0.14em] text-[#525B53]">Property type</label>
            <div className="flex gap-3">
              {[
                { key: "home", label: "Home / Villa", icon: Home, testid: "quote-form-type-home" },
                { key: "business", label: "Business / Commercial", icon: Building2, testid: "quote-form-type-business" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={opt.testid}
                  onClick={() => setPropertyType(opt.key)}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-2xl border-2 px-4 py-3.5 text-sm font-semibold transition-[border-color,background-color,color] duration-300 ${
                    propertyType === opt.key
                      ? "border-[#1C3B2B] bg-[#1C3B2B] text-[#FBF9F5]"
                      : "border-[#E4DDD3] bg-white text-[#525B53] hover:border-[#1C3B2B]/40"
                  }`}
                >
                  <opt.icon size={16} /> {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <label className="mb-3 block text-xs font-semibold uppercase tracking-[0.14em] text-[#525B53]">Services needed</label>
            <div className="flex flex-wrap gap-2.5">
              {SERVICE_OPTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`quote-form-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => toggleService(s)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-[background-color,border-color,color] duration-300 ${
                    services.includes(s)
                      ? "border-[#C85A32] bg-[#C85A32] text-white"
                      : "border-[#E4DDD3] bg-white text-[#525B53] hover:border-[#C85A32]/50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-[#525B53]">
              Details / preferred date
            </label>
            <textarea
              data-testid="quote-form-details-input"
              rows={4}
              value={form.details}
              onChange={(e) => setForm({ ...form, details: e.target.value })}
              placeholder="Tell us about your property, the job size, and when you'd like us to come…"
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            data-testid="quote-form-submit-button"
            type="submit"
            disabled={sending}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#1C3B2B] py-4 text-sm font-semibold text-[#FBF9F5] shadow-[0_18px_40px_-16px_rgba(28,59,43,0.6)] transition-[background-color,transform] duration-300 hover:bg-[#132A1F] hover:scale-[1.02] disabled:opacity-60"
          >
            {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
            {sending ? "Sending…" : "Send Quote Request"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
